import { Component, Vue, Prop, Watch } from 'vue-property-decorator';

import { Room, TimeSlot, Seat, ModalComponents, Company } from '@/definitions';
import { readableDate, findDate } from '@/utilities';
import { Action } from '@/store';

import template from './room-table.vue';

import { nl, enUS } from 'date-fns/locale';
import dateformat from 'dateformat';

import { Datetime } from 'vue-datetime';
import { isAfter, endOfDay } from 'date-fns';

@Component({
    mixins: [template],
    components: { datetime: Datetime }
})
export default class RoomTable extends Vue {
    @Prop() rooms!: Room[];
    dateSelector: string = '';
    today: string = new Date().toISOString();

    @Watch('dateSelector')
    setDateFromString(dateString: string, previous: string) {
        if (this.date.toISOString() !== dateString) {
            this.newDate(0, new Date(dateString));
        }
    }

    @Watch('rooms')
    addDraggableTable(rooms: Room[]) {
        if (rooms.length) {
            const slider = document.querySelector('.rt__rooms') as HTMLElement;
            let isDown = false;
            let startX: number = 0;
            let scrollLeft: number = 0;

            slider.addEventListener('mousedown', (e) => {
                isDown = true;
                slider.classList.add('active');

                startX = e.pageX - slider.offsetLeft;
                scrollLeft = slider.scrollLeft;
            });
            slider.addEventListener('mouseleave', () => {
                isDown = false;
                slider.classList.remove('active');
            });
            slider.addEventListener('mouseup', () => {
                isDown = false;
                slider.classList.remove('active');
            });
            slider.addEventListener('mousemove', (e) => {
                if (!isDown) return;
                e.preventDefault();
                const x = e.pageX - slider.offsetLeft;
                const walk = (x - startX);
                slider.scrollLeft = scrollLeft - walk;
            });
        }
        else if (this.$Store.state.user.isAdmin) {
            const modalData = {
                component: ModalComponents.NO_ROOMS,
                props: {}
            };
            this.$Store.dispatch(Action.SHOW_MODAL, modalData);
        }
    }

    allowedToDelete(ts: TimeSlot) {
        return ts.userId === this.$Store.state.user._id || this.$Store.state.user.isAdmin;
    }

    get date(): Date {
        this.dateSelector = new Date(this.$Store.state.date.date).toISOString();
        return new Date(this.$Store.state.date.date);
    }

    get minDate(): string {
        return dateformat(new Date(), 'yyyy-mm-dd');
    }

    get maximumHour(): number {
        return this.rooms.reduce((maxHour, room) => {
            return (room.bookingEnd > maxHour) ? room.bookingEnd : maxHour;
        }, 0);
    }

    get minimumHour(): number {
        return this.rooms.reduce((minHour, room) => {
            return (room.bookingStart < minHour) ? room.bookingStart : minHour;
        }, this.maximumHour);
    }

    get numOfHours(): number {
        return this.maximumHour - this.minimumHour;
    }

    get headerDate(): {day: string; date: string} {
        const [day, ...date] = readableDate(this.date, this.$i18n.locale === 'nl' ? nl : enUS).split(' ');

        return { day, date: date.join(' ') };
    }

    get company(): Company {
        return this.$Store.state.company.company;
    }

    formatHour(rawHour: number): string {
        const hour = (rawHour < 10) ? `0${rawHour}` : rawHour;
        return `${hour}:00`;
    }

    timeslot(seat: Seat, hour: number): TimeSlot | null {
        return (seat.timeslots as TimeSlot[]).find(slot => (hour >= slot.start && hour < slot.end)) || null;
    }

    isStart(seat: Seat, hour: number): boolean {
        const timeslot = this.timeslot(seat, hour);
        return timeslot ? timeslot.start === hour : false;
    }

    isEnd(seat: Seat, hour: number): boolean {
        const timeslot = this.timeslot(seat, hour);
        return timeslot ? timeslot.end === hour + 1 : false;
    }

    outOfBounds(room: Room, hour: number): boolean {
        return room.bookingStart > hour || room.bookingEnd <= hour;
    }

    maxSelectableHour(room: Room, seatId: string, hour: number): number {
        const timeslots = room.seats.find(s => s.id === seatId)?.timeslots as TimeSlot[];
        const laterTimeslots = timeslots.filter(ts => ts.start > hour) as TimeSlot[];
        return laterTimeslots.reduce((min, ts) => ts.start < min ? ts.start : min, hour);
    }

    handleTimeslotSelect(room: Room, seat: Seat, hour: number): void {
        if (!this.timeslot(seat, hour)) {
            const selectableHours: string[] = new Array(this.maxSelectableHour(room, seat.id as string, room.bookingEnd) - room.bookingStart).fill('00:00')
                .map((h, i) => {
                    const correctHour: number = i + room.bookingStart;
                    return this.visibleHour(correctHour);
                });
                selectableHours.push(this.visibleHour(room.bookingEnd));

            const modalData = {
                component: ModalComponents.APPOINTMENT,
                props: {
                    title: 'Create appointment',
                    date: this.date,
                    room: room._id,
                    seat: seat.id,
                    hour,
                    visibleHour: this.visibleHour(hour),
                    selectableHours
                }
            };
            this.$Store.dispatch(Action.SHOW_MODAL, modalData);
        }
    }

    visibleHour(hour: number): string {
        const h = (hour < 10) ? `0${hour}` : hour;
        return `${h}:00`;
    }

    newDate(amount: number, date?: Date) {
        if (date) {
            this.$emit("newDate", amount, date);
        } else {
            this.$emit("newDate", amount);
        }
    }

    get allowBack(): boolean {
        return isAfter(this.date, endOfDay(findDate(0, new Date(), this.company.calendar)));
    }

    removeAppointment(timeslot: TimeSlot, seat: Seat, room: Room) {
        const modalData = {
            component: ModalComponents.REMOVE_APPOINTMENT,
            props: {
                room: room.friendlyName,
                seat: seat.friendlyName,
                startTime: this.visibleHour(timeslot.start),
                endTime: this.visibleHour(timeslot.end),
                _id: timeslot.id
            }
        };
        this.$Store.dispatch(Action.SHOW_MODAL, modalData);
    }

    openCalendar() {
        const el = document.getElementById('datepicker-field') as HTMLElement;
        el.click();
    }

    changeDateToToday() {
        this.newDate(0, new Date());
    }
}